import type { JSX } from 'react';

import { Alert, Button, Dropdown, Icon, Space, StatusTag, Tag } from '@bookora/ui';
import type { MenuProps } from '@bookora/ui';

import { tourAnchor } from '@/guidance';

import { ALL_BRANCHES_LABEL, PRIMARY_TAG, SWITCHER_HINT } from './copy';
import { useBranchContext, useSwitchBranch } from './hooks';
import { NO_BRANCH_ASSIGNED } from './reasons';

import './branches.css';
// Registers «شعبه‌ها» in the help hub — this module is always mounted by the shell.
import './register';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  «شعبه» — the switcher beside the clinic switcher (multi-branch design §7)
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Rendered only when `GET /branches/mine` has more than one branch — a
 * single-branch clinic (every پایه / حرفه‌ای clinic, and a receptionist who
 * works at one branch of three) never sees it. Options: «همهٔ شعبه‌ها» when the
 * caller may look across branches, then each branch, the primary tagged
 * «اصلی».
 *
 * A switch INVALIDATES the branch-aware caches of this clinic and nothing
 * else (`useSwitchBranch` → `invalidateBranchAwareQueries`): the calendar, the
 * sessions list, the till, stock and reports refetch for the new branch, while
 * the patient file, price list and settings — clinic-wide by design — stay
 * warm. It is never `queryClient.clear()`, which is the clinic switch's
 * (tenant-boundary) tool.
 *
 * A restricted caller the owner has not yet assigned to any branch sees the
 * one sentence that tells them what to do, instead of a calendar that is
 * silently empty.
 */

export interface BranchSwitcherProps {
  /** Same variants as `ClinicSwitcher`: the header (full / compact) or the drawer (block). */
  variant: 'full' | 'compact' | 'block';
}

export function BranchSwitcher({ variant }: BranchSwitcherProps): JSX.Element | null {
  const ctx = useBranchContext();
  const switchTo = useSwitchBranch();

  if (ctx.unassigned) {
    return variant === 'block' ? (
      <Alert
        type="warning"
        showIcon
        title={NO_BRANCH_ASSIGNED}
        className="bk-branch-switch__alert"
      />
    ) : (
      <StatusTag tone="warning" hint={NO_BRANCH_ASSIGNED} icon="warning">
        بدون شعبه
      </StatusTag>
    );
  }

  if (!ctx.isMulti) return null;

  const currentLabel = ctx.activeBranch?.name ?? ALL_BRANCHES_LABEL;
  const selectedKey = ctx.activeBranchId ?? ALL_KEY;

  const items: MenuProps['items'] = [
    ...(ctx.allowAll ? [{ key: ALL_KEY, label: ALL_BRANCHES_LABEL }] : []),
    ...ctx.branches.map((branch) => ({
      key: branch.id,
      label: (
        <Space size={6}>
          <span>{branch.name}</span>
          {branch.isPrimary ? <Tag>{PRIMARY_TAG}</Tag> : null}
        </Space>
      ),
      // The menu row is what a keyboard and a screen reader land on; the tag
      // is read as part of the name.
      'aria-label': branch.isPrimary ? `${branch.name} (${PRIMARY_TAG})` : branch.name,
    })),
  ];

  return (
    <Dropdown
      menu={{
        items,
        selectable: true,
        selectedKeys: [selectedKey],
        onClick: ({ key }) => switchTo(key === ALL_KEY ? null : key),
      }}
      trigger={['click']}
      placement="bottom-start"
    >
      <Button
        {...tourAnchor('branches.switcher')}
        type="text"
        block={variant === 'block'}
        className={`bk-branch-switch bk-branch-switch--${variant}`}
        icon={variant === 'compact' ? null : <Icon name="location" />}
        aria-label={`شعبهٔ فعال: ${currentLabel}`}
        hint={SWITCHER_HINT}
      >
        <span className="bk-truncate bk-branch-switch__name">
          {variant === 'block' ? `شعبه: ${currentLabel}` : currentLabel}
        </span>
        {variant === 'block' ? null : <Icon name="chevron-down" size={16} />}
      </Button>
    </Dropdown>
  );
}

/** The menu key of «همهٔ شعبه‌ها» (a menu key cannot be null). */
const ALL_KEY = '__all__';
